import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { CatalogFeature } from "@/types/catalog-feature";
import {
  CreateSubscriptionPlanPayload,
  PlanFeatureValuePayload,
} from "@/types/subscription-plan";
import { useFeatures } from "../hooks/useFeatures";
import { useCreateSubscriptionPlan } from "../hooks/useSubscriptionPlanMutations";
import { PlanFeaturesBadgesField } from "./PlanFeaturesBadgesField";

interface CreateDefaultPlanDialogProps {
  open: boolean;
  onClose: () => void;
}

function buildFeaturePayload(
  ids: string[],
  featureOptions: CatalogFeature[],
  featureValueById: Readonly<Partial<Record<string, string>>>,
): PlanFeatureValuePayload[] {
  const featureById = new Map(
    featureOptions.map((feature) => [feature.id, feature]),
  );

  return ids.map((id) => {
    const feature = featureById.get(id);
    const rawValue =
      feature?.type === "NUMBER"
        ? (featureValueById[id] ?? String(feature.limit ?? "")).trim()
        : (featureValueById[id] ?? "true");

    return {
      featureId: id,
      value: rawValue,
    };
  });
}

export const CreateDefaultPlanDialog: React.FC<
  CreateDefaultPlanDialogProps
> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const { data: features = [], isLoading: featuresLoading } = useFeatures(open);
  const { mutateAsync, isPending } = useCreateSubscriptionPlan();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priceInput, setPriceInput] = useState("");
  const [currency, setCurrency] = useState("usd");
  const [isDefault, setIsDefault] = useState(true);
  const [enableCoreFeatures, setEnableCoreFeatures] = useState(true);
  const [selectedFeatureIds, setSelectedFeatureIds] = useState<string[]>([]);
  const [featureValueById, setFeatureValueById] = useState<
    Record<string, string>
  >({});

  useEffect(() => {
    if (open) {
      setTitle("");
      setDescription("");
      setPriceInput("");
      setCurrency("usd");
      setIsDefault(true);
      setEnableCoreFeatures(true);
      setSelectedFeatureIds([]);
      setFeatureValueById({});
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedTitle = title.trim();
    if (trimmedTitle === "") {
      toast.error(t("subscriptions.plans.default.create.validation.title"));
      return;
    }

    const price = Number.parseFloat(priceInput.trim());
    if (!Number.isFinite(price) || price < 0) {
      toast.error(t("subscriptions.plans.default.create.validation.price"));
      return;
    }

    const payload: CreateSubscriptionPlanPayload = {
      title: trimmedTitle,
      price,
      currency: currency.trim() === "" ? "usd" : currency.trim().toLowerCase(),
      isDefault,
      enableCoreFeatures,
    };

    const descTrimmed = description.trim();
    if (descTrimmed !== "") {
      payload.description = descTrimmed;
    }

    const featuresPayload = buildFeaturePayload(
      selectedFeatureIds,
      featureOptions,
      featureValueById,
    );
    if (featuresPayload.length > 0) {
      payload.features = featuresPayload;
    }

    try {
      await mutateAsync(payload);
      toast.success(t("subscriptions.plans.default.create.successToast"));
      onClose();
    } catch {
      toast.error(t("subscriptions.plans.default.create.errorToast"));
    }
  };

  const featureOptions: CatalogFeature[] = features;

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {t("subscriptions.plans.default.create.title")}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <Input
            label={t("subscriptions.plans.default.create.planTitle")}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            autoComplete="off"
          />
          <Textarea
            label={t("subscriptions.plans.default.create.description")}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <div className="grid grid-cols-2 gap-3">
            <Input
              type="number"
              inputMode="decimal"
              step="any"
              min={0}
              label={t("subscriptions.plans.default.create.price")}
              value={priceInput}
              onChange={(e) => setPriceInput(e.target.value)}
            />
            <Input
              label={t("subscriptions.plans.default.create.currency")}
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              maxLength={3}
            />
          </div>

          <div className="border-border flex items-center justify-between rounded-lg border px-3 py-2">
            <span className="text-foreground text-sm font-medium">
              {t("subscriptions.plans.default.create.isDefault")}
            </span>
            <Switch checked={isDefault} onCheckedChange={setIsDefault} />
          </div>

          <div className="border-border flex items-center justify-between rounded-lg border px-3 py-2">
            <span className="text-foreground text-sm font-medium">
              {t("subscriptions.plans.featureField.includeCore")}
            </span>
            <Switch
              checked={enableCoreFeatures}
              onCheckedChange={setEnableCoreFeatures}
            />
          </div>

          <PlanFeaturesBadgesField
            featureOptions={featureOptions}
            selectedFeatureIds={selectedFeatureIds}
            onSelectedFeatureIdsChange={setSelectedFeatureIds}
            featuresLoading={featuresLoading}
            featureValueById={featureValueById}
            onFeatureValueByIdChange={setFeatureValueById}
            labels={{
              section: t("subscriptions.plans.default.create.features"),
              addPlaceholder: t(
                "subscriptions.plans.default.create.selectFeature",
              ),
              removeFeature: t(
                "subscriptions.plans.default.create.removeFeature",
              ),
              enabled: t("subscriptions.plans.featureField.enabled"),
              value: t("subscriptions.plans.featureField.value"),
            }}
          />

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={isPending}
              onClick={onClose}
            >
              {t("subscriptions.plans.default.create.cancel")}
            </Button>
            <Button type="submit" disabled={isPending}>
              {t("subscriptions.plans.default.create.submit")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
